<template>
	<BaseModalNew :hasFooter="true" @close="close">
		<template #title>
			{{ $t('WEBAPP_WHY_TO_WATCH_TITLE', { titleName }) }}
		</template>

		<div class="form">
			<label>
				<span class="label">{{ $t('WEBAPP_WHY_TO_WATCH_HEADLINE_LABEL') }}</span>

				<InputText
					v-model="headline"
					class="input"
					type="text"
					maxlength="100"
					:placeholder="$t('WEBAPP_WHY_TO_WATCH_HEADLINE_PLACEHOLDER')"
					@focus="() => trackWtw('headlineFocused')"
					@blur="() => trackWtw('headlineBlurred', { checkArgs: headline })"
				/>

				<span class="note" :class="{ error: headline?.length >= 100 }"> {{ headline?.length ?? 0 }}/100 </span>
			</label>

			<label>
				<span class="label">{{ $t('WEBAPP_WHY_TO_WATCH_BODY_LABEL') }}</span>

				<InputTextArea
					class="input textarea-input"
					v-model="body"
					cols="30"
					rows="6"
					wrap="hard"
					maxlength="1100"
					required
					@focus="() => trackWtw('bodyFocused')"
					@blur="() => trackWtw('bodyBlurred', { checkArgs: body })"
					:placeholder="$t('WEBAPP_WHY_TO_WATCH_BODY_PLACEHOLDER')"
				/>

				<span class="note" :class="{ error: body.length >= 1100 }">
					{{ body?.length ?? 0 }}/1100 (Min. 60)
				</span>
			</label>

			<label class="form__provider">
				<span class="label">{{ $t('WEBAPP_WHERE_WATCHED') }}</span>

				<div class="input form__provider__dropdown" @click="toggleProviderSearch">
					<div v-if="watchedOn" class="form__provider__dropdown__package">
						<PictureComp
							class="provider-icon"
							:key="watchedOn?.packageId"
							:imageUrl="selectedProviderImageUrl"
							imageProfile="provider_icon"
							:alt="watchedOn?.clearName"
						/>

						<span>
							{{ watchedOn.packageId === 0 ? $t('WEBAPP_CINEMA') : watchedOn.clearName }}
						</span>
					</div>

					<div v-else class="placeholder">{{ $t('WEBAPP_SELECT_STREAMING_SERVICE') }}</div>
					<FontAwesomeIcon :icon="displayProviderSearch ? faChevronUp : faChevronDown" />
				</div>

				<div v-if="displayProviderSearch" class="form__provider__list">
					<div class="form__provider__list__search">
						<SearchBar
							v-model="providerSearch"
							:placeholder="$t('WEBAPP_SEARCH')"
							:clearable="true"
							theme="light"
						/>
					</div>

					<div class="provider-list">
						<PackageRow
							v-for="p of providers"
							:packageItem="{ ...p, selected: watchedOn?.packageId === p.packageId }"
							:key="p.id"
							@onPackageToggle="providerClickHandler(p)"
						/>
					</div>
				</div>
			</label>

			<div v-if="!hasProfile" class="user-info">
				<InputAvatar @open="trackWtw('uploadImageClick')" @fileUpdated="handleAvatarBase64" />

				<label>
					<span class="label">{{ $t('WEBAPP_USER_PROFILE_NAME_LABEL') }}</span>

					<InputText
						v-model="fullName"
						class="input"
						maxlength="70"
						:placeholder="$t('WEBAPP_USER_PROFILE_NAME_PLACEHOLDER')"
						required
						@focus="trackWtw('nameFocused')"
						@blur="() => trackWtw('nameBlurred', { checkArgs: fullName })"
					/>
				</label>
			</div>
		</div>

		<template #footer>
			<BasicButton
				class="submit"
				color="primary"
				:class="{ disabled: !submitValidation || isSubmitting }"
				@click="handleSubmit"
			>
				{{ editMode ? $t('WEBAPP_UPDATE_RECOMMENDATION') : $t('WEBAPP_ADD_RECOMMENDATION') }}
			</BasicButton>
		</template>
	</BaseModalNew>
</template>

<script setup lang="ts">
import type { Package } from '@/@types/graphql-types';
import type { TextRecommendationFragment } from '@/features/why-to-watch/graphql/fragments/TextRecommendation.fragment';
import type { TitleDetail } from '@/interfaces/title-details-graphql';

import type {
	CreateTextRecommendationAndProfileMutation,
	CreateTextRecommendationAndProfileMutationVariables,
} from '@/features/why-to-watch/graphql/mutations/CreateTextRecommendationAndProfile.mutation';
import type {
	UpdateTextRecommendationMutation,
	UpdateTextRecommendationMutationVariables,
} from '@/features/why-to-watch/graphql/mutations/UpdateTextRecommendation.mutation';

import { CreateTextRecommendationAndProfileDocument } from '@/features/why-to-watch/graphql/mutations/CreateTextRecommendationAndProfile.mutation';
import { UpdateTextRecommendationDocument } from '@/features/why-to-watch/graphql/mutations/UpdateTextRecommendation.mutation';

import { MonetizationType } from '@/@types/graphql-types';

import BaseModalNew from '@/components/modals/BaseModalNew.vue';
import BasicButton from '@/ui-components/BasicButton.vue';
import InputAvatar from '@/features/ui/components/InputAvatar.vue';
import InputText from '@/features/ui/components/InputText.vue';
import InputTextArea from '@/features/ui/components/InputTextArea.vue';
import PackageRow from '@/components/modals/package-selection/PackageRow.vue';
import PictureComp from '@/components/picture/PictureComp.vue';
import SearchBar from '@/features/search/components/SearchBar.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import { isMobile } from '@/helpers/ui-helper';
import { toastSuccess, toastError } from '@/helpers/vfm/toast-helper';

import { computed, ref } from 'vue';
import {
	CreateTextRecommendationDocument,
	CreateTextRecommendationMutation,
	CreateTextRecommendationMutationVariables,
} from '../graphql/mutations/CreateTextRecommendation.mutation';
import { faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { useWtwTracking } from '../tracking';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';

const props = withDefaults(
	defineProps<{
		title: TitleDetail;
		formData?: Partial<TextRecommendationFragment> | null;
		editMode?: boolean;
	}>(),
	{ editMode: false }
);

const emit = defineEmits<{
	(e: 'close'): void;
}>();

const { jwConfig } = useRuntimeConfig().public;

const userStore = storeToRefs(useUserStore());
const { country, language } = storeToRefs(useLanguageStore());
const { t } = useI18n();

// TRACKING
const { trackWtw, trackWtwDisabledClick } = useWtwTracking({ title: () => props.title });

const providerSearch = ref<string>('');
const displayProviderSearch = ref(false);
const isSubmitting = ref(false);

// Form Data
const id = ref<string>(props.formData?.id ?? '');
const headline = ref<string>(props.formData?.headline ?? '');
const body = ref<string>(props.formData?.body ?? '');
const watchedOn = ref(props.formData?.watchedOn ?? null);

const fullName = ref<string>(userStore.userInfo.value.name ?? '');
const avatarBase64 = ref<string | null>(userStore.userInfo.value.photoURL ?? null);

function handleAvatarBase64(base64: string) {
	trackWtw('imageSelected');
	avatarBase64.value = base64;
}

const titleName = computed(() =>
	props.title.__typename === 'Season' && !!props.title.show
		? `${props.title.show.content.title} - ${props.title.content.title}`
		: props.title.content.title
);

const uniqueProviders = computed(() => {
	const uniqueProviderMap = new Map();

	props.title.offers
		.filter(offer => offer.monetizationType !== MonetizationType.Cinema)
		.forEach(offer => {
			if (!uniqueProviderMap.has(offer.package.shortName)) {
				uniqueProviderMap.set(offer.package.shortName, offer.package);
			}
		});

	const uniqueProviders = Array.from(uniqueProviderMap.values());

	// Add cinema provider to end of list if movie
	if (props.title.__typename === 'Movie') {
		uniqueProviders.push({
			providerShortName: undefined,
			clearName: t('WEBAPP_CINEMA'),
		});
	}

	return uniqueProviders;
});

const providers = computed(() =>
	uniqueProviders.value.filter(p => p.clearName.toUpperCase().includes(providerSearch.value.toUpperCase()))
);

const selectedProviderImageUrl = computed(() => {
	if (watchedOn.value?.packageId === 0) {
		return 'https://static.justwatch.com/static/compile_jw/assets/jw-icon-badge.png';
	}

	return watchedOn.value?.icon
		? `${jwConfig.IMAGESCALER_URL}${watchedOn.value?.icon.replace('{profile}', 's40')}`
		: '';
});

const hasProfile = computed(() => {
	const profileUUID = userStore.profileInfo.value?.uuid;

	return profileUUID != null && profileUUID !== '';
});

const submitValidation = computed(() => {
	const wtwValid = headline.value && body.value && body.value.length >= 60;
	const profileValid = fullName.value.trim().length > 2;

	if (props.editMode) return wtwValid;

	return hasProfile.value ? wtwValid : profileValid && wtwValid;
});

function toggleProviderSearch() {
	if (displayProviderSearch.value === false) {
		trackWtw('providerDropdownClicked');
	}
	displayProviderSearch.value = !displayProviderSearch.value;
}

function providerClickHandler(provider: Package) {
	trackWtw('providerSelected');

	watchedOn.value = provider;
	toggleProviderSearch();
}

const createRecommendationAndProfile = useMutation<
	CreateTextRecommendationAndProfileMutation,
	CreateTextRecommendationAndProfileMutationVariables
>(CreateTextRecommendationAndProfileDocument, {
	refetchQueries: ['GetTextRecommendations'],
});

const createRecommendation = useMutation<CreateTextRecommendationMutation, CreateTextRecommendationMutationVariables>(
	CreateTextRecommendationDocument,
	{
		refetchQueries: ['GetTextRecommendations'],
	}
);

const updateRecommendation = useMutation<UpdateTextRecommendationMutation, UpdateTextRecommendationMutationVariables>(
	UpdateTextRecommendationDocument,
	{
		refetchQueries: ['GetTextRecommendations'],
	}
);

async function handleSubmit() {
	if (!submitValidation.value || isSubmitting.value) {
		trackWtwDisabledClick();
		return;
	}

	isSubmitting.value = true;

	if (props.editMode) {
		await updateRecommendation.mutate({
			input: {
				id: id.value,
				headline: headline.value,
				body: body.value,
				watchedOn: watchedOn.value?.packageId ?? 0,
			},
			country: country.value,
			language: language.value,
		});
	} else {
		if (hasProfile.value) {
			await createRecommendation.mutate({
				input: {
					textRecommendation: {
						headline: headline.value,
						body: body.value,
						watchedOn: watchedOn.value?.packageId ?? 0,
					},
					jwEntityID: props.title.id,
					country: country.value,
				},
				country: country.value,
				language: language.value,
			});
		} else {
			await createRecommendationAndProfile.mutate({
				input: {
					name: fullName.value,
					avatarBase64: avatarBase64.value,
					textRecommendation: {
						headline: headline.value,
						body: body.value,
						watchedOn: watchedOn.value?.packageId ?? 0,
					},
					jwEntityID: props.title.id,
					country: country.value,
				},
				country: country.value,
				language: language.value,
			});
		}
	}

	isSubmitting.value = false;

	trackWtw('addWhyToWatch');
	emit('close');
}

function close() {
	trackWtw('modalClosed');
	emit('close');
}

updateRecommendation.onDone(() => onSuccess(t('WEBAPP_RECOMMENDATION_UPDATED_SUCCESS')));
updateRecommendation.onError(error => {
	console.error(error);
	onError(t('WEBAPP_RECOMMENDATION_UPDATED_ERROR'));
});

createRecommendation.onDone(() => onSuccess(t('WEBAPP_RECOMMENDATION_ADDED_SUCCESS')));
createRecommendation.onError(error => {
	console.error(error);
	onError(t('WEBAPP_RECOMMENDATION_ADDED_ERROR'));
});

createRecommendationAndProfile.onDone(() => onSuccess(t('WEBAPP_RECOMMENDATION_ADDED_SUCCESS')));
createRecommendationAndProfile.onError(error => {
	console.error(error);
	onError(t('WEBAPP_RECOMMENDATION_ADDED_ERROR'));
});

function onSuccess(message: string) {
	toastSuccess({ message, duration: 3000, position: isMobile() ? 'top' : 'bottom' });
}

function onError(message: string) {
	toastError({ message, duration: 3000, position: isMobile() ? 'top' : 'bottom' });
}
</script>

<style lang="scss" scoped>
.header-info {
	display: flex;
	align-items: baseline;
	gap: 8px;
	margin-inline-end: 16px;

	@media (max-width: 512px) {
		margin-inline-end: 8px;
	}

	&__action {
		color: var(--link-link-medium, #78a6b8);
		font-size: 16px;
		font-style: normal;
		font-weight: 400;
		line-height: 16px;
		letter-spacing: 0.24px;
	}

	&__page {
		color: var(--text-text-low, #727f92);
		font-size: 12px;
		font-style: normal;
		font-weight: 400;
		line-height: 12px;
		letter-spacing: 0.18px;
	}
}

.form {
	min-height: 100%;
	display: flex;
	flex-direction: column;
	gap: 24px;

	&__provider {
		display: flex;
		flex-direction: column;
		margin-bottom: 16px;

		&__dropdown {
			display: flex;
			padding: 12px 16px;
			align-items: center;
			justify-content: space-between !important;
			gap: 12px;
			border-radius: 8px;
			background-color: var(--background-bg-medium, #111924);
			line-height: 16px;
			letter-spacing: 0.24px;

			&:hover {
				cursor: pointer !important;
			}

			&__package {
				display: flex;
				align-items: center;
				gap: 8px;
			}
		}

		&__list {
			background: var(--background-bg-medium, #111924);
			padding-bottom: 16px;
			border-radius: 8px;
			margin-top: 8px;

			&__search {
				margin: 0 16px;
			}
		}

		.provider-list {
			max-height: 300px;
			overflow-y: scroll;
			overflow-x: hidden;
		}
	}

	&__button {
		width: 100%;
		display: flex;
		flex-direction: column;

		:deep(button) {
			width: 100%;
		}
	}

	.user-info {
		display: flex;
		gap: 24px;
		align-items: flex-start;

		&:last-child {
			margin-bottom: 16px;
		}
	}

	label:has(input, textarea) {
		flex-grow: 1;
	}

	.label {
		color: var(--text-text-medium, #c6c8cd);
		overflow: hidden;
		text-overflow: ellipsis;
		line-height: 16px;
		font-size: 14px;
		letter-spacing: 0.24px;
		font-weight: 600;
	}

	.placeholder {
		color: var(--text-text-low, #909398);
	}

	.input {
		margin-top: 8px;
	}

	.textarea-input {
		overflow-wrap: break-word;
	}

	.note {
		color: var(--text-text-low, #727f92);
		font-size: 12px;
		font-style: normal;
		font-weight: 400;
		line-height: 16px;
		letter-spacing: 0.18px;
		display: inline-block;
		margin-top: 8px;

		&.error {
			color: var(--text-text-error, #ff4d4f);
		}
	}

	.provider-icon {
		display: block;
		width: 32px;
		height: 32px;

		:deep(.picture-comp__img) {
			object-fit: cover;
			border-radius: 4px;
		}
	}
}

.submit {
	width: 100%;
}
</style>
